import { useAuth } from '@/stores/auth.store';
import { DirectiveBinding } from 'vue';

/**
 * Custom directive to show/hide elements based on the user's role.
 *
 * Any random role can be passed to only allow super admins.
 *
 * Omitting a role will allow any authenticated user to see the element.
 *
 * Uses the v-auth-else directive as a direct replacement for v-else.
 *
 * @example
 * ```vue
 * <button v-auth="'admin'">Admin Only</button>
 *
 * <button v-auth>Any User</button>
 * <button v-auth-else>Unauthenticated user</button>
 * ```
 */
export const vAuth = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const authStore = useAuth();
    const requiredRole = binding.value as string;
    const hasRole = authStore.user?.permissions.some((p) => {
      if (!requiredRole) {
        return true;
      }
      const nested = p.permissions?.map((p) => p.role.toString()) ?? [];
      const role = p.role.toString();

      return [...role, ...nested].includes(requiredRole);
    });
    const isAuthorized =
      authStore.user && authStore.state === 'AUTHENTICATED' && (authStore.user.isSuperAdmin || hasRole);

    const nextSibling = el.nextElementSibling as HTMLElement;

    if (!isAuthorized) {
      el.style.display = 'none';

      if (nextSibling?.hasAttribute('v-auth-else')) {
        nextSibling.style.display = '';
      }
    } else {
      el.style.display = '';

      if (nextSibling?.hasAttribute('v-auth-else')) {
        nextSibling.style.display = 'none';
      }
    }
  },
};

export const vAuthElse = {
  mounted(el: HTMLElement) {
    el.style.display = 'none';
  },
};
